import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import styles from './live_widgets.module.css'
import {
  BaseWidget,
  FormFieldsProps,
  LiveWidgetsNavigationProps,
  LiveWidgetsProps,
  PreviewProps,
  ResetHandler,
  SubmitHandler,
  UrlCopyComponentProps,
  WidgetConfiguratorProps,
  WidgetDetailsProps,
  WidgetFieldProps,
  WidgetName,
  WidgetNameList,
  WidgetParamsKey,
  Widgets,
} from './types'
import { BrowserRouter, NavLink, Route } from 'react-router-dom'
import {
  CopyComponent,
  FormAutoSubmitter,
  FormColorInput,
  FormGroup,
  FormRadio,
  JSONFetcher,
  LogoSpinner,
  PopoverIcon,
  QueryHelper,
  postValues,
  report,
  themeColor,
  underscore,
  useJsonApi,
} from 'shared'
import { FastField, Form, Formik, FormikProps } from 'formik'
import { FontFamily } from './widget_field/font_family'
import { FontWeight } from './widget_field/font_weight'

import * as Yup from 'yup'

export const LiveWidgets = ({ backUrl, baseApiUrl }: LiveWidgetsProps): JSX.Element | null => {
  const { data: widgetNames } = useJsonApi<WidgetNameList>(baseApiUrl)
  if (!widgetNames) return null

  return (
    <BrowserRouter>
      <Route
        path={'/*/:name'}
        render={(props) => {
          const widgetName = props.match.params.name as WidgetName

          return (
            <div className="row">
              <div className="col-md-6 admin-sidebar">
                <LiveWidgetsNavigation backUrl={backUrl} currentWidgetName={widgetName} widgetNames={widgetNames} />
              </div>
              <div className="col-md-17 admin-content-box bold-labels">
                <WidgetDetails baseApiUrl={baseApiUrl} widgetName={widgetName} />
              </div>
            </div>
          )
        }}
      />
    </BrowserRouter>
  )
}

const LiveWidgetsNavigation = ({ backUrl, currentWidgetName, widgetNames }: LiveWidgetsNavigationProps) => {
  return (
    <nav className="generic-manage-sidebar-menu" aria-labelledby="nav-headline">
      <ul className="menu">
        <a className="p-0!" href={backUrl}>
          <i className="fas fa-chevron-left mr-1" />
          <span>{i18n.t(`fundraising_events.manage.live_widgets.backlink_to_settings`)}</span>
        </a>
        <span id="nav-headline" className="block text-xl font-bold mt-5 mb-2">
          {i18n.t(`fundraising_events.manage.live_widgets.nav_headline`)}
        </span>
        {widgetNames.map((name) => (
          <li key={name} className={`${name === currentWidgetName ? 'active' : ''}`}>
            <NavLink to={name}>
              {i18n.t(`fundraising_events.manage.live_widgets.${underscore(name)}_headline_html`)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

const WidgetDetails = ({ baseApiUrl, widgetName }: WidgetDetailsProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: widget, loading } = useJsonApi<Extract<Widgets, BaseWidget<typeof widgetName, any>>>(
    `${baseApiUrl}/${widgetName}`
  )

  // Note: this un-mounts formik when switching widgets, preventing stale props
  if (!widget || loading) return <div style={{ height: 2000 }} />

  return (
    <>
      <h2>{i18n.t(`fundraising_events.manage.live_widgets.${underscore(widget.name)}_headline_html`)}</h2>
      <p>{i18n.t(`fundraising_events.manage.live_widgets.${underscore(widget.name)}_introduction_html`)}</p>
      <WidgetConfigurator baseApiUrl={baseApiUrl} widget={widget} />
    </>
  )
}

function useHideDemo(widgetName: string, duration: number) {
  const isDonationAlert = useMemo(() => widgetName === 'donation-alert', [widgetName])
  const durationRef = useRef(duration)
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    durationRef.current = duration
  }, [duration])
  const [hiddenDemo, setHiddenDemo] = useState(isDonationAlert)
  const showDemo = useCallback(() => setHiddenDemo(false), [])
  const hideDemo = useCallback(() => setHiddenDemo(true), [])
  useEffect(() => {
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current)
    if (isDonationAlert && !hiddenDemo) {
      // The first demo alert always appears after 3 seconds. So the preview has to be present for at least 3 seconds (3000) + duration.
      previewTimeoutRef.current = setTimeout(
        () => {
          setHiddenDemo(true)
        },
        5000 + (durationRef.current ?? 0) * 1000
      )
    }
    return () => {
      previewTimeoutRef.current && clearTimeout(previewTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenDemo])
  return [hiddenDemo, showDemo, hideDemo] as const
}

const sanitizeParams = (params: Widgets['params']) =>
  (Object.keys(params) as (keyof typeof params)[]).reduce(
    (agg, key) => {
      agg[key] = params[key] ?? ''
      return agg
    },
    {} as typeof params
  )

const WidgetConfigurator = ({ baseApiUrl, widget }: WidgetConfiguratorProps) => {
  const initialValues = useMemo(() => sanitizeParams(widget.params), [widget.params])
  const largePreview = widget.name === 'donation-alert' || widget.name === 'standard' ? true : false

  const formikRef = useRef<FormikProps<typeof initialValues> | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [streambotUrlParams, setStreambotUrlParams] = useState<Record<string, any>>(widget.params)
  const streambotUrl = widget.baseStreambotUrl + QueryHelper.toQuery(streambotUrlParams)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  const [hiddenDemo, showDemo, hideDemo] = useHideDemo(widget.name, (formikRef.current?.values as any)?.duration)

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        gif: Yup.string().url(i18n.t('misc.invalid_url')).matches(/.gif$/, i18n.t('misc.invalid_gif')),
        mp3: Yup.string().url(i18n.t('misc.invalid_url')).matches(/.mp3$/, i18n.t('misc.invalid_mp3')),
      }),
    []
  )

  const submitHandler: SubmitHandler = useCallback(
    (values, formik) => {
      const widget_settings = postValues(values)
      widget.name === 'donation-alert' && hideDemo()

      JSONFetcher.put({
        url: `${baseApiUrl}/${widget.name}`,
        body: { widget_settings },
        success: () => {
          formik.setSubmitting(false)
          setStreambotUrlParams(widget_settings)
        },
        onError: (e) => {
          report(e)
          setTimeout(() => window.location.reload(), 200)
        },
      })
    },
    [baseApiUrl, setStreambotUrlParams, widget.name, hideDemo]
  )
  const resetHandler: ResetHandler = useCallback(
    (formik) => {
      JSONFetcher.delete({
        url: `${baseApiUrl}/${widget.name}`,
        success: ({ defaults }) => {
          formik.setValues(defaults)
        },
        onError: (e) => {
          report(e)
          setTimeout(() => window.location.reload(), 200)
        },
      })
    },
    [baseApiUrl, widget.name]
  )

  // ensure inputs without default value are controlled, too
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitHandler}
      innerRef={formikRef}
      validationSchema={validationSchema}
    >
      {(formikProps) => (
        <Form>
          <Preview
            height={largePreview ? 270 : 150}
            containerHeight={largePreview ? 290 : 170}
            streambotUrl={streambotUrl}
            loading={formikProps.isSubmitting}
            name={widget.name}
            hiddenDemo={hiddenDemo}
            showDemo={showDemo}
          />

          <FormFields formikProps={formikProps} widget={widget} resetHandler={resetHandler} />

          <FormAutoSubmitter />

          <UrlCopyComponent streambotUrl={streambotUrl} />
        </Form>
      )}
    </Formik>
  )
}

const Preview = ({
  height,
  containerHeight: minHeight,
  streambotUrl,
  loading,
  name,
  hiddenDemo,
  showDemo,
}: PreviewProps) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (!e) return
        const boxShadow = e.intersectionRatio < 1 ? '0 10px 10px -10px #919191' : 'none'
        const ele = e.target as HTMLElement
        ele.style.boxShadow = boxShadow
      },

      { threshold: [1] }
    )
    if (ref.current) observer.observe(ref.current)
  }, [])

  return (
    <div ref={ref} className="sticky -top-1 bg-white z-50">
      <div className="flex items-center justify-between mb-2">
        <h3 className={styles.previewHeadline}>{i18n.t('fundraising_events.manage.live_widgets.preview_headline')}</h3>
        {name !== 'donation-alert' && (
          <a href={streambotUrl} target="_blank" rel="noreferrer">
            {i18n.t('fundraising_events.manage.live_widgets.live_preview_link')}
          </a>
        )}
      </div>
      <div className="generic-preview-container p-205 flex items-center justify-center" style={{ minHeight }}>
        {loading ? (
          <LogoSpinner color={themeColor('green-500')} />
        ) : hiddenDemo ? (
          <button type="button" className="btn btn-primary btn-small" onClick={showDemo}>
            {i18n.t('fundraising_events.manage.live_widgets.play_demo_button')}
          </button>
        ) : (
          <iframe
            title="widget_preview"
            marginHeight={0}
            marginWidth={0}
            src={streambotUrl + (streambotUrl.includes('?') ? '&demo=true' : '?demo=true')}
            width="100%"
            height={height}
            frameBorder="0"
            // Without the following line Chrome blocks the sound
            allow="autoplay"
            scrolling="yes"
          />
        )}
      </div>
    </div>
  )
}

const FormFields = ({ formikProps, widget, resetHandler }: FormFieldsProps) => {
  const handleReset = useCallback(() => resetHandler(formikProps), [formikProps, resetHandler])

  const withoutBackgroundImage =
    'withoutBackgroundImage' in formikProps.values && formikProps.values.withoutBackgroundImage === 'true'
  //Show textColorInput only for standard widgets without background color
  const showTextColorInput = !('withoutBackgroundImage' in widget.params) || withoutBackgroundImage

  return (
    <>
      <div className="flex items-center justify-between">
        <h3>{i18n.t('fundraising_events.manage.live_widgets.configurator.headline')}</h3>
        <button className="simulated-link" type="button" onClick={handleReset}>
          <i className="fas fa-undo mr-1" aria-hidden="true" />
          <span>{i18n.t('fundraising_events.manage.live_widgets.configurator.reset_button')}</span>
        </button>
      </div>

      <div className="desktop:w-2/3">
        <WidgetField widget={widget} name="backgroundColor" component={FormColorInput} transparentOption />
        <WidgetField widget={widget} name="progressBackgroundColor" component={FormColorInput} transparentOption />
        <WidgetField widget={widget} name="progressColor" component={FormColorInput} />
        {'withoutBackgroundImage' in widget.params && (
          <>
            <label htmlFor="display" className="control-label">
              {i18n.t('fundraising_events.manage.live_widgets.configurator.without_background_image_label')}
            </label>
            <div className="flex gap-4">
              <WidgetField
                widget={widget}
                name="withoutBackgroundImage"
                component={FormRadio}
                className={classNames(withoutBackgroundImage && 'pb-3', 'cursor-pointer')}
                value="false"
                label={i18n.t(
                  'fundraising_events.manage.live_widgets.configurator.without_background_image_value_false'
                )}
              />
              <WidgetField
                widget={widget}
                name="withoutBackgroundImage"
                component={FormRadio}
                className={classNames(withoutBackgroundImage && 'pb-3', 'cursor-pointer')}
                value="true"
                label={i18n.t(
                  'fundraising_events.manage.live_widgets.configurator.without_background_image_value_true'
                )}
              />
            </div>
          </>
        )}
        <>{showTextColorInput && <WidgetField widget={widget} name="textColor" component={FormColorInput} />}</>

        <WidgetField widget={widget} name="nicknameColor" component={FormColorInput} />
        <WidgetField widget={widget} name="headline" component={FormGroup} />
        <FontFamily widget={widget} formikProps={formikProps} />
        <FontWeight widget={widget} formikProps={formikProps} />
        <WidgetField widget={widget} name="textAlign" component={FormGroup}>
          {'textAlign' in formikProps.values && (
            <select
              id="textAlign"
              name="textAlign"
              value={formikProps.values.textAlign || 'center'}
              onChange={formikProps.handleChange}
            >
              <option value="left">
                {i18n.t('fundraising_events.manage.live_widgets.configurator.text_align_value_left')}
              </option>
              <option value="center">
                {i18n.t('fundraising_events.manage.live_widgets.configurator.text_align_value_center')}
              </option>
              <option value="right">
                {i18n.t('fundraising_events.manage.live_widgets.configurator.text_align_value_right')}
              </option>
            </select>
          )}
        </WidgetField>
        {'display' in widget.params && (
          <label htmlFor="display" className="control-label">
            {i18n.t('fundraising_events.manage.live_widgets.configurator.display_label')}
          </label>
        )}
        <div className="flex gap-4">
          <WidgetField
            widget={widget}
            name="display"
            component={FormRadio}
            className="pb-3 cursor-pointer"
            value="both"
            label={i18n.t('fundraising_events.manage.live_widgets.configurator.display_value_both')}
          />
          <WidgetField
            widget={widget}
            name="display"
            component={FormRadio}
            className="pb-3 cursor-pointer"
            value="bar"
            label={i18n.t('fundraising_events.manage.live_widgets.configurator.display_value_bar')}
          />
          <WidgetField
            widget={widget}
            name="display"
            component={FormRadio}
            className="pb-3 cursor-pointer"
            value="text"
            label={i18n.t('fundraising_events.manage.live_widgets.configurator.display_value_text')}
          />
        </div>
        {'collected' in formikProps.values && (
          <WidgetField widget={widget} name="collected" component={FormGroup}>
            <select
              id="collected"
              name="collected"
              value={formikProps.values.collected || 'true'}
              onChange={formikProps.handleChange}
            >
              <option value={''}>
                {i18n.t('fundraising_events.manage.live_widgets.configurator.collected_value_with_text')}
              </option>
              <option value={'false'}>
                {i18n.t('fundraising_events.manage.live_widgets.configurator.collected_value_only_data')}
              </option>
            </select>
          </WidgetField>
        )}
        <WidgetField widget={widget} name="maxCount" component={FormGroup} type="number" min="1" />
        {'list' in widget.params && widget.params.list && (
          <>
            <label htmlFor="list" className="control-label">
              {i18n.t('fundraising_events.manage.live_widgets.configurator.list_label')}
            </label>

            <div className="flex gap-4">
              <WidgetField
                widget={widget}
                name="list"
                component={FormRadio}
                className="pb-3 cursor-pointer"
                value="false"
                label={i18n.t('fundraising_events.manage.live_widgets.configurator.list_value_false')}
              />
              <WidgetField
                widget={widget}
                name="list"
                component={FormRadio}
                className="pb-3 cursor-pointer"
                value="true"
                label={i18n.t('fundraising_events.manage.live_widgets.configurator.list_value_true')}
              />
            </div>
          </>
        )}
        {'list' in formikProps.values && formikProps.values.list === 'true' && (
          <WidgetField
            widget={widget}
            name="width"
            component={FormGroup}
            // the default of this one includes the unit unfortunately,
            // maybe clean up in streambot
            // type="number"
          />
        )}
        <WidgetField widget={widget} name="gifHeight" component={FormGroup} type="number" />
        <WidgetField widget={widget} name="duration" component={FormGroup} type="number" />
        <WidgetField widget={widget} name="wording" component={FormGroup} />
        <WidgetField widget={widget} name="hashtags" component={FormGroup} />
        <WidgetField widget={widget} name="gif" component={FormGroup} hasPopover />
        <WidgetField widget={widget} name="mp3" component={FormGroup} hasPopover />
        <WidgetField
          widget={widget}
          name="volume"
          id="volume"
          component={FormGroup}
          type="range"
          step="0.1"
          min="0"
          max="1"
          value={+(('volume' in formikProps.values && formikProps.values.volume) || '0')}
        />
        {'volume' in formikProps.values && (
          <output htmlFor="volume" className="block text-right relative -top-4">
            {+formikProps.values.volume * 100}%
          </output>
        )}
      </div>
    </>
  )
}

export const WidgetField = <K extends WidgetParamsKey>({
  widget,
  name,
  labelClass,
  groupClass,
  hasPopover,
  ...fieldProps
}: WidgetFieldProps<K>) => {
  if (!(name in widget.params)) return null

  // FastFields only get updates about their own field data, e.g. value,
  // and can be used as long as there is no interdependency between fields,
  // or between fields and the general state of the form.
  // https://formik.org/docs/api/fastfield
  return (
    <div className={classNames({ relative: hasPopover })}>
      <FastField
        name={name}
        labelClass={labelClass}
        groupClass={groupClass}
        label={i18n.t(`fundraising_events.manage.live_widgets.configurator.${underscore(name)}_label`)}
        {...fieldProps}
      />
      {hasPopover && (
        <div className="absolute right-0 top-0">
          <div className="relative">
            <PopoverIcon
              i18nKey={`fundraising_events.manage.live_widgets.configurator.${underscore(name)}_popover`}
              popoverId={`tooltip-for-widget-${underscore(name)}`}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const UrlCopyComponent = ({ streambotUrl }: UrlCopyComponentProps) => {
  return (
    <>
      <h3 className="mt-12">{i18n.t('fundraising_events.manage.live_widgets.url_headline')}</h3>
      <CopyComponent contentType="code" ctaCopy={i18n.t('misc.copy_link')} value={streambotUrl} />
    </>
  )
}
